import React, { useState, useEffect } from "react";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { ReactSearchAutocomplete } from "react-search-autocomplete";
import { useDispatch, useSelector } from "react-redux";
import { SAGA_ACTIONS } from "../../_config";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useAppUserDataSelector } from "src/_common/hooks/selectors/userSelector";
import { useHistory } from "react-router-dom";

interface VideoCVFiltersProps {
  onApplyFilters: (filters: { keywords: string; location: string; country_id: string; skills: string }) => void;
  onResetFilters: () => void;
  context?: "videoCv" | "candidate";
}

const VideoCVFilters: React.FC<VideoCVFiltersProps> = ({ onApplyFilters, onResetFilters, context = "videoCv" }) => {
  const { t } = useTranslation("video-cv");
  const dispatch = useDispatch();
  const toast = useToaster();
  const history = useHistory();
  const userData = useAppUserDataSelector();

  // Check if user is on free plan
  const isFreePlan = userData?.subscription?.price === 0;

  // Local state for filters
  const [filters, setFilters] = useState({
    keywords: "",
    location: "",
    experienceLevel: [] as string[],
    skills: "",
  });

  // Country autocomplete states
  const [countrySelectKey, setCountrySelectKey] = useState(0);
  const [selectedCountryId, setSelectedCountryId] = useState<string>("");
  const [selectedCountryName, setSelectedCountryName] = useState<string>("");
  const [hasCountryInteracted, setHasCountryInteracted] = useState(false);
  const [countries, setCountries] = useState<Array<{ id: string; name: string }>>([]);

  // Skills autocomplete states
  const [skillsSelectKey, setSkillsSelectKey] = useState(0);
  const [selectedSkillId, setSelectedSkillId] = useState<string>("");
  const [selectedSkillName, setSelectedSkillName] = useState<string>("");
  const [hasSkillInteracted, setHasSkillInteracted] = useState(false);
  const [skillsList, setSkillsList] = useState<Array<{ id: string; name: string }>>([]);

  // Redux selectors
  const { countriesNew } = useSelector((state: any) => ({
    countriesNew: state.global.countriesNew,
  }));

  const { skills } = useSelector((state: any) => ({
    skills: state.global.skills,
  }));

  // Format countries from Redux when data changes
  useEffect(() => {
    if (countriesNew && countriesNew.length > 0) {
      const formattedCountries = countriesNew.map((country: any) => ({
        id: country.id.toString(),
        name: country.name,
      }));
      setCountries(formattedCountries);
    }
  }, [countriesNew]);

  // Format skills from Redux when data changes
  useEffect(() => {
    if (skills && skills.length > 0) {
      const formattedSkills = skills.map((skill: any) => ({
        id: skill.id.toString(),
        name: skill.name,
      }));
      setSkillsList(formattedSkills);
    }
  }, [skills]);

  // Fetch initial skills
  useEffect(() => {
    dispatch({
      type: SAGA_ACTIONS.COMMON.SKILLS,
      payload: { limit: 30, offset: 0 },
    });
  }, [dispatch]);

  // Fetch countries from API
  const getCountries = (searchName: string = "") => {
    const params: any = { limit: 30 };
    if (searchName.trim()) {
      params.name = searchName.trim();
    }
    dispatch({
      type: SAGA_ACTIONS.COMMON.COUNTRIES_NEW,
      payload: params,
    });
  };

  // Handle country selection
  const handleCountrySelect = (item: any) => {
    setSelectedCountryId(item.id);
    setSelectedCountryName(item.name);
    handleFilterChange("location", item.name);
    // Reset interaction and remount to close dropdown
    setHasCountryInteracted(false);
    setCountrySelectKey((k) => k + 1);
  };

  // Fetch skills from API
  const getSkills = (searchName: string = "") => {
    const params: any = { limit: 30, offset: 0 };
    if (searchName.trim()) {
      params.q = searchName.trim();
    }
    dispatch({
      type: SAGA_ACTIONS.COMMON.SKILLS,
      payload: params,
    });
  };

  // Handle skill selection
  const handleSkillSelect = (item: any) => {
    setSelectedSkillId(item.id);
    setSelectedSkillName(item.name);
    handleFilterChange("skills", item.name);
    // Reset interaction and remount to close dropdown
    setHasSkillInteracted(false);
    setSkillsSelectKey((k) => k + 1);
  };

  const handleFilterChange = (filterType: string, value: string | string[]) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  const handleApplyFilters = () => {
    // Check if at least one filter is selected
    const hasAnyFilter = filters.keywords.trim() !== "" || selectedCountryId !== "" || selectedSkillName !== "";

    if (!hasAnyFilter) {
      toast.warning(t("videoCv.filters.selectAtLeastOne", "Please select at least one filter to search"));
      return;
    }

    onApplyFilters({
      keywords: filters.keywords,
      location: filters.location,
      country_id: selectedCountryId,
      skills: selectedSkillName,
    });
  };

  const handleResetFilters = () => {
    // Check if any filters are currently set
    const hasActiveFilters =
      filters.keywords.trim() !== "" ||
      filters.location.trim() !== "" ||
      filters.skills.trim() !== "" ||
      selectedCountryId !== "" ||
      selectedSkillName !== "";

    // Reset all filter states
    setFilters({
      keywords: "",
      location: "",
      experienceLevel: [],
      skills: "",
    });
    setSelectedCountryId("");
    setSelectedCountryName("");
    setHasCountryInteracted(false);
    setCountrySelectKey((k) => k + 1);
    setSelectedSkillId("");
    setSelectedSkillName("");
    setHasSkillInteracted(false);
    setSkillsSelectKey((k) => k + 1);

    // Only notify parent component (trigger API call) if filters were actually set
    if (hasActiveFilters) {
      onResetFilters();
    }
  };

  const hasActiveFilters = filters.keywords.trim() !== "" || selectedCountryId !== "" || selectedSkillName !== "";

  return (
    <div className="video-cv-filters-outer">
      <div className="video-cv-filters">
        <div className="filters-header">
          <h3>{t("videoCv.filters.title", "Filters")}</h3>
          <button className={`reset-button ${!hasActiveFilters ? "d-none" : ""}`} onClick={handleResetFilters}>
            {t("videoCv.filters.reset", "Reset")}
          </button>
        </div>

        <div className="filter-group">
          <label>{t("videoCv.filters.keywords", "Keywords")}</label>
          <div className="input-with-icon">
            <i className="search-icon fa fa-search"></i>
            <input
              type="text"
              placeholder={
                context === "videoCv"
                  ? t("videoCv.filters.keywordsPlaceholder")
                  : t("videoCv.filters.candidatePlaceholder")
              }
              value={filters.keywords}
              onChange={(e) => handleFilterChange("keywords", e.target.value)}
            />
          </div>
        </div>

        <div className="filter-group position-relative z-3">
          <label>{t("videoCv.filters.location", "Country")}</label>
          <div className="autocomplete-container">
            <ReactSearchAutocomplete
              key={countrySelectKey}
              items={hasCountryInteracted ? countries : []}
              onSearch={(string, results) => {
                setHasCountryInteracted(true);
                if (string.length > 0) {
                  getCountries(string);
                } else {
                  getCountries();
                }
              }}
              onFocus={() => {
                if (!hasCountryInteracted) {
                  setHasCountryInteracted(true);
                  getCountries();
                }
              }}
              onSelect={handleCountrySelect}
              onClear={() => {
                setSelectedCountryId("");
                setSelectedCountryName("");
                handleFilterChange("location", "");
                setHasCountryInteracted(false);
                setCountrySelectKey((k) => k + 1);
                getCountries();
              }}
              placeholder={t("videoCv.filters.locationPlaceholder", "Select country")}
              inputSearchString={selectedCountryName || ""}
              showIcon={false}
              showNoResults={false}
              autoFocus={false}
              styling={{
                height: "45px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                backgroundColor: "white",
                boxShadow: "none",
                hoverBackgroundColor: "#f3f4f6",
                color: "#374151",
                fontSize: "14px",
                fontFamily: "inherit",
                iconColor: "#6b7280",
                lineColor: "#e5e7eb",
                placeholderColor: "#9ca3af",
                clearIconMargin: "3px 8px 0 0",
                zIndex: 2,
              }}
            />
          </div>
        </div>

        <div className="filter-group position-relative z-0">
          <label>{t("videoCv.filters.skills", "Skills")}</label>
          <div className="autocomplete-container">
            <ReactSearchAutocomplete
              key={skillsSelectKey}
              items={hasSkillInteracted ? skillsList : []}
              onSearch={(string, results) => {
                setHasSkillInteracted(true);
                if (string.length > 0) {
                  getSkills(string);
                } else {
                  getSkills();
                }
              }}
              onFocus={() => {
                if (!hasSkillInteracted) {
                  setHasSkillInteracted(true);
                  getSkills();
                }
              }}
              onSelect={handleSkillSelect}
              onClear={() => {
                setSelectedSkillId("");
                setSelectedSkillName("");
                handleFilterChange("skills", "");
                setHasSkillInteracted(false);
                setSkillsSelectKey((k) => k + 1);
                getSkills();
              }}
              placeholder={t("videoCv.filters.skillsPlaceholder", "e.g., React, python")}
              inputSearchString={selectedSkillName || ""}
              showIcon={false}
              showNoResults={false}
              autoFocus={false}
              styling={{
                height: "45px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                backgroundColor: "white",
                boxShadow: "none",
                hoverBackgroundColor: "#f3f4f6",
                color: "#374151",
                fontSize: "14px",
                fontFamily: "inherit",
                iconColor: "#6b7280",
                lineColor: "#e5e7eb",
                placeholderColor: "#9ca3af",
                clearIconMargin: "3px 8px 0 0",
                zIndex: 2,
              }}
            />
          </div>
        </div>

        <div className="filter-actions">
          <button className="btn-cancel d-none" onClick={handleResetFilters}>
            {t("videoCv.filters.cancel", "Cancel")}
          </button>
          <button className="btn-preview" onClick={handleApplyFilters}>
            {t("videoCv.filters.applyFilter", "Apply Filter")}
          </button>
        </div>
      </div>

      {/* Free Plan Upgrade Notice */}
      {isFreePlan && (
        <div className="free-plan-notice mt-3 p-3 bg-light border rounded text-center">
          <div className="mb-2">
            <i
              className={`fa ${context === "videoCv" ? "fa-video" : "fa-search"} text-muted mb-2`}
              style={{ fontSize: "20px" }}
            ></i>
            <p className="mb-1 fw-semibold" style={{ fontSize: "14px" }}>
              {context === "videoCv"
                ? t("videoCv.filters.freePlanTitleVideoCv", "Only 10 Video CVs are available on the Free plan")
                : t("videoCv.filters.freePlanTitleCandidate", "You're viewing 10 candidates on the Free plan")}
            </p>
            <p className="text-muted mb-3" style={{ fontSize: "12px" }}>
              {t("videoCv.filters.freePlanSubtitle", "Upgrade to view more candidates.")}
            </p>
          </div>
          <button className="btn-preview" onClick={() => history.push("/settings/subscriptions")}>
            {t("videoCv.filters.upgradePlan")}
          </button>
        </div>
      )}
    </div>
  );
};

export default VideoCVFilters;
